import { always, tryCatch } from 'ramda';
import { History } from 'history';
import { ApolloClient } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError, ErrorLink } from '@apollo/client/link/error';
import { hrefFor } from '../../../routers';
import { isDevelopment } from '../../../lib/isEnv';
import { getCookies } from '../../../hooks/useCookies';
import { getUserFromCookies } from '../../../hooks/useUser';
import { createCache, initializeCache } from '../cache';
import { getSearchApiHeaders } from '../getSearchApiHeaders';
import { createCommonConfiguration, isUnauthorizedError } from './common';

const createClientErrorHandler = (history: History): ErrorLink.ErrorHandler => ({
  graphQLErrors,
  networkError,
  operation,
  response
}) => {
  if (isUnauthorizedError({ graphQLErrors, networkError })) {
    if (response) response.errors = undefined;
    history.replace(hrefFor.LogoutPage());
  }

  // eslint-disable-next-line no-console
  console.error(`Apollo: GraphQL Error during operation "${operation.operationName}"\n%o`, {
    graphQLErrors,
    networkError,
    operation,
    response
  });
};

export const createClientSideApolloClient = (history: History) => {
  const cache = createCache();
  const headersLink = setContext((_, { headers }) => {
    const cookies = getCookies();
    const searchApiHeaders = getSearchApiHeaders(cookies);
    const { accessToken } = getUserFromCookies();

    return {
      headers: {
        ...headers,
        ...searchApiHeaders,
        authorization: accessToken ? `Bearer ${accessToken}` : ''
      }
    };
  });

  initializeCache(cache, {});

  const commonConfiguration = createCommonConfiguration();

  return new ApolloClient({
    ...commonConfiguration,
    link: onError(createClientErrorHandler(history))
      .concat(headersLink)
      .concat(commonConfiguration.link),
    cache: cache.restore(window.__APOLLO_STATE__),
    connectToDevTools: isDevelopment()
      ? true
      : tryCatch(devTools => JSON.parse(devTools), always(false))(window.__ENV__?.DEV_TOOLS)
  });
};
